/**
 * useActiveRuleset() — read the currently-active GameRuleset for
 * the running game-kind from public.game_rulesets, falling back to
 * the in-memory DEFAULT_POKER_RULESET when the DB is unreachable
 * or hasn't been queried yet.
 *
 * P1.3 (StackedStreak-bue) of the GameRuleset roadmap. Mirrors the
 * useFeatureFlag pattern (module-level cache, single one-shot fetch
 * shared across all hook subscribers, fallback never throws) so the
 * app never bricks on a Supabase outage at boot — gameplay starts
 * with the default and silently upgrades to the DB ruleset when
 * the response lands.
 *
 * Realtime (P2.2): once /admin/game-tuning ships, an admin
 * publishing a new ruleset propagates to all open clients within
 * ~2s via a postgres_changes subscription on game_rulesets. The
 * subscription updates the module-level cache + notifies React
 * subscribers so UIs reading the active ruleset re-render
 * automatically.
 *
 * In-flight games: this hook reads the CURRENT active ruleset. If
 * an admin publishes a new ruleset mid-game (after P2 lands), an
 * already-running game session must NOT switch rules — useGameState
 * captures the ruleset returned from this hook at session start
 * and holds it for the duration. P1.4 implements that capture.
 *
 * Example:
 *   const ruleset = useActiveRuleset();
 *   const duration = ruleset.timing.gameDurationSec;
 */

import { useEffect, useState } from 'react';
import { supabase } from '../supabase/client';
import {
  DEFAULT_POKER_RULESET,
  isGameRuleset,
  type GameRuleset,
} from '@/types/ruleset';
import { assignVariant, type ExperimentVariantInput } from '@/lib/experiments/assignVariant';

// ─── Module-level shared state ───

let cachedRuleset: GameRuleset = DEFAULT_POKER_RULESET;
// P4.1: per-segment overrides cache, keyed by segment label.
// Populated alongside the active-ruleset fetch; refreshed by the
// realtime subscription on segment_overrides too.
const cachedSegmentOverrides = new Map<string, GameRuleset>();

// P4.2: enabled experiments + their variants + the rulesets each
// variant points at. Refreshed alongside segment overrides and via
// realtime subscription on experiments / experiment_variants.
interface CachedExperiment {
  id: string;
  key: string;
  variants: ExperimentVariantInput[];
  // Variant id → resolved GameRuleset. Pre-resolved at refresh time
  // so getActiveRulesetSync stays synchronous and cheap.
  rulesetByVariantId: Map<string, GameRuleset>;
}
const cachedExperiments = new Map<string, CachedExperiment>();
let loadPromise: Promise<void> | null = null;
let realtimeChannel: ReturnType<typeof supabase.channel> | null = null;
const subscribers = new Set<() => void>();

function notify() {
  subscribers.forEach((cb) => {
    try {
      cb();
    } catch {
      // subscriber error must not break the others
    }
  });
}

/**
 * Hydrate a GameRuleset from a DB row. The row stores meta-fields
 * (id, name, game_kind, schema_version) as columns and the tunable
 * surface (grid/timing/scoring/modifierSpawn/matchRule) as a `config`
 * jsonb blob. Reassemble into the flat GameRuleset shape callers
 * expect from src/types/ruleset.ts.
 *
 * Returns null if the row is missing required fields or the config
 * jsonb is malformed — defensive against a partially-populated row
 * created in some future migration that drifts from this schema.
 */
function hydrateFromRow(row: {
  id?: string;
  name?: string;
  game_kind?: string;
  schema_version?: number;
  config?: unknown;
}): GameRuleset | null {
  if (
    typeof row.id !== 'string' ||
    typeof row.name !== 'string' ||
    typeof row.game_kind !== 'string' ||
    typeof row.schema_version !== 'number' ||
    typeof row.config !== 'object' ||
    row.config === null
  ) {
    return null;
  }
  // We only support schema_version 1 today. A future bump should
  // either run a migration first or add a translation step here.
  if (row.schema_version !== 1) {
    return null;
  }

  const cfg = row.config as Record<string, unknown>;
  const candidate: GameRuleset = {
    id: row.id,
    name: row.name,
    schemaVersion: 1,
    gameKind: row.game_kind,
    grid: cfg.grid as GameRuleset['grid'],
    timing: cfg.timing as GameRuleset['timing'],
    scoring: cfg.scoring as GameRuleset['scoring'],
    modifierSpawn: cfg.modifierSpawn as GameRuleset['modifierSpawn'],
    matchRule: cfg.matchRule as GameRuleset['matchRule'],
  };
  return isGameRuleset(candidate) ? candidate : null;
}

/**
 * Refresh the segment-override cache for a given game_kind. Pulls
 * all overrides + the rulesets they reference in a single round-
 * trip. Called from ensureLoaded after the active-ruleset fetch
 * lands, and re-called by the realtime subscription whenever a
 * row in segment_overrides changes.
 */
/**
 * Refresh the experiments cache for a given game_kind. Pulls all
 * enabled experiments + their variants + the rulesets each variant
 * points at, all in one round-trip. Pre-resolves the variant →
 * ruleset map so getActiveRulesetSync can do its assignment +
 * lookup synchronously without extra queries.
 */
async function refreshExperiments(gameKind: string): Promise<void> {
  try {
    const { data, error } = await supabase
      .from('experiments')
      .select(`
        id, key, enabled,
        experiment_variants (
          id, key, traffic_pct, ruleset_id,
          game_rulesets (id, name, game_kind, schema_version, config)
        )
      `)
      .eq('game_kind', gameKind)
      .eq('enabled', true);
    if (error || !data) return;

    cachedExperiments.clear();
    for (const exp of data as Array<{
      id: string;
      key: string;
      experiment_variants: Array<{
        id: string;
        key: string;
        traffic_pct: number;
        ruleset_id: string;
        game_rulesets: unknown;
      }>;
    }>) {
      if (!Array.isArray(exp.experiment_variants) || exp.experiment_variants.length === 0) continue;
      const variants: ExperimentVariantInput[] = [];
      const rulesetByVariantId = new Map<string, GameRuleset>();
      for (const v of exp.experiment_variants) {
        variants.push({
          id: v.id,
          key: v.key,
          traffic_pct: v.traffic_pct,
          ruleset_id: v.ruleset_id,
        });
        const hydrated = hydrateFromRow(v.game_rulesets as Parameters<typeof hydrateFromRow>[0]);
        if (hydrated) rulesetByVariantId.set(v.id, hydrated);
      }
      cachedExperiments.set(exp.id, {
        id: exp.id,
        key: exp.key,
        variants,
        rulesetByVariantId,
      });
    }
    notify();
  } catch {
    /* refresh failure must not break gameplay */
  }
}

async function refreshSegmentOverrides(gameKind: string): Promise<void> {
  try {
    const { data, error } = await supabase
      .from('segment_overrides')
      .select('segment, ruleset_id, game_rulesets(id, name, game_kind, schema_version, config)')
      .eq('game_kind', gameKind);
    if (error || !data) return;

    cachedSegmentOverrides.clear();
    for (const row of data) {
      // The supabase-js select-with-relation returns the FK-joined
      // row inline. Type as 'any' here to avoid leaking the
      // relation-shape type into our public surface.
      const r = (row as { segment: string; game_rulesets: unknown }).game_rulesets;
      const hydrated = hydrateFromRow(r as Parameters<typeof hydrateFromRow>[0]);
      if (hydrated) {
        cachedSegmentOverrides.set((row as { segment: string }).segment, hydrated);
      }
    }
    notify();
  } catch {
    /* override refresh failure is non-fatal — fall back to global active */
  }
}

function ensureLoaded(gameKind: string): Promise<void> {
  if (loadPromise) return loadPromise;
  loadPromise = (async () => {
    try {
      const { data, error } = await supabase
        .from('game_rulesets')
        .select('id, name, game_kind, schema_version, config, is_active')
        .eq('game_kind', gameKind)
        .eq('is_active', true)
        .limit(1)
        .maybeSingle();

      if (error || !data) {
        // Reset so a future call can retry — don't permanently cache
        // the failure. Keep the in-memory default in `cachedRuleset`.
        loadPromise = null;
        return;
      }

      const hydrated = hydrateFromRow(data);
      if (hydrated) {
        cachedRuleset = hydrated;
        notify();
      } else {
        // Row exists but doesn't validate. Keep the default; clear
        // the load promise so a fresh call retries (the row may be
        // mid-publish and missing fields temporarily).
        loadPromise = null;
      }

      // Pull segment overrides + experiments in parallel-ish.
      // Both are separate tables, so we kick off the fetches as
      // soon as the global active is in cache. Experiment cache
      // is mandatory for getActiveRulesetSync's assignment path
      // when a userId is provided.
      void refreshSegmentOverrides(gameKind);
      void refreshExperiments(gameKind);

      // Once the initial fetch lands, attach the realtime subscription.
      // Doing this AFTER the first successful read avoids a race where
      // a publish event arrives before we know what the prior active
      // row was (we'd refresh-and-get-the-same-row, which is a no-op
      // but wastes a roundtrip).
      ensureRealtime(gameKind);
    } catch {
      loadPromise = null;
    }
  })();
  return loadPromise;
}

/**
 * Subscribe to game_rulesets changes via Supabase Realtime (P2.2).
 *
 * When an admin publishes a new ruleset (publish_game_ruleset RPC
 * flips is_active across two rows in a single statement), we get
 * one or more postgres_changes events. We don't try to interpret
 * each event individually — instead we just refetch the active
 * ruleset and notify subscribers if it changed. That's idempotent,
 * cheap, and survives any ordering oddity in the event stream.
 *
 * Idempotent: only attaches once per page session. Survives reconnect
 * automatically (the supabase-js channel handles WebSocket retry).
 */
function ensureRealtime(gameKind: string): void {
  if (realtimeChannel) return;

  realtimeChannel = supabase
    .channel(`game_rulesets_realtime:${gameKind}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'game_rulesets' },
      async () => {
        // Refetch the active row and update the cache if it changed.
        try {
          const { data, error } = await supabase
            .from('game_rulesets')
            .select('id, name, game_kind, schema_version, config, is_active')
            .eq('game_kind', gameKind)
            .eq('is_active', true)
            .limit(1)
            .maybeSingle();
          if (error || !data) return;
          const hydrated = hydrateFromRow(data);
          if (!hydrated) return;
          if (hydrated.id !== cachedRuleset.id) {
            cachedRuleset = hydrated;
            notify();
          }
          // The active row may have changed in a way that affects
          // segment override resolution (e.g., ruleset was deleted),
          // so refresh segment overrides too.
          void refreshSegmentOverrides(gameKind);
        } catch {
          /* refresh failure must not break the channel */
        }
      },
    )
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'segment_overrides' },
      () => {
        // Any change to the override table → refresh and notify.
        void refreshSegmentOverrides(gameKind);
      },
    )
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'experiments' },
      () => {
        // Experiment enable/disable → refresh + notify. The
        // refresh re-pulls enabled experiments only, so a flip
        // to enabled=false drops the experiment from the cache.
        void refreshExperiments(gameKind);
      },
    )
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'experiment_variants' },
      () => {
        // Variant traffic_pct change or new variant → refresh.
        void refreshExperiments(gameKind);
      },
    )
    .subscribe();
}

/**
 * Read-only React hook returning the active ruleset. Re-renders
 * subscribers whenever the cached ruleset is replaced (after the
 * DB fetch resolves, or — once P2.2 lands — on a realtime change).
 */
export function useActiveRuleset(gameKind: string = 'poker_blitz'): GameRuleset {
  const [value, setValue] = useState<GameRuleset>(cachedRuleset);

  useEffect(() => {
    void ensureLoaded(gameKind);
    const update = () => setValue(cachedRuleset);
    subscribers.add(update);
    update();
    return () => {
      subscribers.delete(update);
    };
  }, [gameKind]);

  return value;
}

/**
 * Synchronous read for non-React contexts (game-logic instantiation,
 * analytics events). Returns the cached ruleset, which is the default
 * until ensureRulesetReady() resolves.
 *
 * P4.1: optional `segment` parameter walks the segment_overrides
 * cache before falling back to the global active.
 *
 * Resolution order (HIGHEST priority first):
 *   1. Global active ruleset for the gameKind (the default). This
 *      function only handles segment overrides + global; for
 *      experiment-aware resolution use resolveActiveRulesetForUser.
 */
export function getActiveRulesetSync(segment?: string): GameRuleset {
  if (segment) {
    const override = cachedSegmentOverrides.get(segment);
    if (override) return override;
  }
  return cachedRuleset;
}

/**
 * Full resolution path with experiment + segment + global fallback
 * (P4.2). Returns the chosen ruleset PLUS the experiment context
 * the caller should stamp on game_sessions for retention analysis.
 *
 * Resolution order (HIGHEST → LOWEST):
 *   1. Active experiment the user is enrolled in (deterministic
 *      hash assignment via assignVariant). Experiments win because
 *      they're explicit, time-bound, and the whole point of running
 *      one is to NOT have a different rule layered on top.
 *   2. Segment override (P4.1) if `segment` is provided.
 *   3. Global active ruleset for the kind.
 *
 * userId is required for experiment enrollment; without it we can't
 * compute a stable hash and fall through to segment / global.
 */
export interface ResolvedRuleset {
  ruleset: GameRuleset;
  experimentId?: string;
  experimentVariantId?: string;
  segment?: string;
}

export function resolveActiveRulesetForUser(
  userId: string | null,
  segment?: string,
): ResolvedRuleset {
  // 1. Try active experiments. The first experiment the user is
  // enrolled in wins; multi-experiment overlap isn't supported
  // yet (Phase 4 follow-up if it's ever needed).
  if (userId) {
    for (const exp of cachedExperiments.values()) {
      const assigned = assignVariant(userId, exp.id, exp.key, exp.variants);
      if (!assigned) continue;
      const rs = exp.rulesetByVariantId.get(assigned.variant_id);
      if (rs) {
        return {
          ruleset: rs,
          experimentId: assigned.experiment_id,
          experimentVariantId: assigned.variant_id,
          segment,
        };
      }
    }
  }

  // 2. Segment override.
  if (segment) {
    const override = cachedSegmentOverrides.get(segment);
    if (override) {
      return { ruleset: override, segment };
    }
  }

  // 3. Global active.
  return { ruleset: cachedRuleset, segment };
}

/**
 * Block until the active ruleset is loaded from DB (or fails over
 * to the default). Useful in startup paths that want to record the
 * authoritative ruleset id on the first session even if the DB
 * fetch races with game-start.
 */
export function ensureRulesetReady(gameKind: string = 'poker_blitz'): Promise<void> {
  return ensureLoaded(gameKind);
}

/**
 * Test hook — wipes module-level cache + load promise so a fresh
 * test can simulate a cold start. Not exported through any public
 * barrel; only test files import this directly.
 */
export function __resetUseActiveRulesetForTests(): void {
  cachedRuleset = DEFAULT_POKER_RULESET;
  cachedSegmentOverrides.clear();
  cachedExperiments.clear();
  loadPromise = null;
  if (realtimeChannel) {
    void supabase.removeChannel(realtimeChannel);
    realtimeChannel = null;
  }
  subscribers.clear();
}
